/**
 * Core Preview Contract Index route catalog.
 *
 * Static catalog of all preview-only routes. Every route has auth requirement,
 * preview-only flag, supported actions, expected safety flags, and safe example
 * payload. No real execution, no provider call, no mutation.
 */
import { createDefaultCorePreviewContractIndexPolicyFlags } from "./contract-index-policy.js";
function createBaseRoute(routeId, route, method, moduleId, options) {
    return {
        routeId,
        route,
        method,
        moduleId,
        authRequired: options.authRequired,
        previewOnly: true,
        supportedActions: options.supportedActions,
        expectedSafetyFlags: createDefaultCorePreviewContractIndexPolicyFlags(),
        forbiddenCapabilities: options.forbiddenCapabilities,
        responseShapeSummary: options.responseShapeSummary,
        smokeTestKind: options.smokeTestKind,
        appUseCases: options.appUseCases,
        safeExamplePayload: options.safeExamplePayload,
        safeExpectedStatusWithoutAuth: options.safeExpectedStatusWithoutAuth,
        safeExpectedStatusWithAuth: options.safeExpectedStatusWithAuth,
        notes: options.notes,
    };
}
const GATEWAY_PREVIEW = createBaseRoute("gateway_preview", "POST /v1/gateway/preview", "POST", "gateway_preview", {
    authRequired: false,
    supportedActions: ["gateway_preview"],
    forbiddenCapabilities: ["real_gateway_routing", "external_network", "payment", "send"],
    responseShapeSummary: "{ previewOnly, mode, requestedMode, provider, model, blocked, requiresApproval, mutationExecuted, providerCallExecuted, realExecutionPerformed }",
    smokeTestKind: "preview_only",
    appUseCases: ["Smart Print edge compatibility preview", "Safe gateway request validation"],
    safeExamplePayload: { query: "Smart Print estimate preview", dryRunOnly: true },
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Unauthenticated preview-only route; always returns safe metadata and never routes real traffic.",
});
const GATEWAY_OBSERVABILITY_PREVIEW_STATUS = createBaseRoute("gateway_observability_preview_status", "GET /v1/gateway/observability/preview-status", "GET", "gateway_observability", {
    authRequired: true,
    supportedActions: ["observability_status"],
    forbiddenCapabilities: ["external_observability_export", "persistent_event_store_write"],
    responseShapeSummary: "{ ok, previewOnly, eventCount, maxEventCount, recentEvents, redactionApplied, storage, persistentStoreEnabled, externalNetworkEnabled, mutationExecuted, providerCallExecuted, realExecutionPerformed }",
    smokeTestKind: "token_protected",
    appUseCases: ["Owner panel observability status check"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Token-protected read-only route; returns in-memory safe observability summary.",
});
const GATEWAY_OBSERVABILITY_RECENT_EVENTS = createBaseRoute("gateway_observability_recent_events", "GET /v1/gateway/observability/recent-events", "GET", "gateway_observability", {
    authRequired: true,
    supportedActions: ["recent_events"],
    forbiddenCapabilities: ["external_observability_export", "persistent_event_store_write"],
    responseShapeSummary: "{ previewOnly, events, redactionApplied, eventCount, capped }",
    smokeTestKind: "token_protected",
    appUseCases: ["Owner panel recent gateway events preview"],
    safeExamplePayload: { limit: 10 },
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Token-protected read-only route; returns redacted and capped in-memory events.",
});
const SECURITY_BUMBLEBEE_PREVIEW_INGEST = createBaseRoute("security_bumblebee_preview_ingest", "POST /v1/security/bumblebee/preview-ingest", "POST", "security_inventory_preview", {
    authRequired: false,
    supportedActions: ["preview_ingest"],
    forbiddenCapabilities: ["real_remediation", "security_rule_update", "external_network", "mutation"],
    responseShapeSummary: "{ previewOnly, accepted, redacted, parsedRows, errors, policyFlags }",
    smokeTestKind: "preview_only",
    appUseCases: ["Security inventory NDJSON preview ingest"],
    safeExamplePayload: { lines: ["{\"event\":\"test\"}"], dryRunOnly: true },
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview-only security inventory ingest; no real rule update or remediation.",
});
const SECURITY_BUMBLEBEE_PREVIEW_ALERT = createBaseRoute("security_bumblebee_preview_alert", "POST /v1/security/bumblebee/preview-alert", "POST", "security_alert_preview", {
    authRequired: false,
    supportedActions: ["preview_alert"],
    forbiddenCapabilities: ["whatsapp_send", "github_issue_create", "deploy_gate_change", "external_network"],
    responseShapeSummary: "{ previewOnly, alertPreview, workflowSteps, blocked, policyFlags }",
    smokeTestKind: "preview_only",
    appUseCases: ["Security alert workflow preview"],
    safeExamplePayload: { alertType: "suspicious_token", severity: "high" },
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview-only alert workflow; no real WhatsApp/GitHub/create/deploy gate change.",
});
const MCP_READONLY_TOOLS_PREVIEW = createBaseRoute("mcp_readonly_tools_preview", "POST /v1/mcp/read-only-tools/preview", "POST", "mcp_readonly_tools_preview", {
    authRequired: false,
    supportedActions: ["calculator", "knowledge_search", "report_preview", "status_preview"],
    forbiddenCapabilities: ["mcp_live_call", "tool_execution", "file_system_mutation", "external_network"],
    responseShapeSummary: "{ previewOnly, toolId, result, redactedInput, policyFlags }",
    smokeTestKind: "preview_only",
    appUseCases: ["Read-only calculator", "Knowledge lookup preview"],
    safeExamplePayload: { toolId: "calculator", intent: "add", userMessage: "2 + 2" },
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Read-only preview tools; no live MCP/tool execution or network call.",
});
const AUDIT_STORE_PREVIEW = createBaseRoute("audit_store_preview", "POST /v1/audit-store/preview", "POST", "audit_store_preview", {
    authRequired: false,
    supportedActions: ["append_preview", "query_preview", "summary_preview"],
    forbiddenCapabilities: ["audit_write", "audit_export", "audit_retention_apply", "persistent_storage"],
    responseShapeSummary: "{ previewOnly, auditEventId, redactedSummary, persisted, policyFlags }",
    smokeTestKind: "preview_only",
    appUseCases: ["Audit append preview", "Audit summary preview"],
    safeExamplePayload: { eventType: "test_event", dryRunOnly: true },
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview-only audit store; persisted is always false and no real storage write.",
});
const MEMORY_RETRIEVAL_PREVIEW = createBaseRoute("memory_retrieval_preview", "POST /v1/memory-retrieval/preview", "POST", "memory_retrieval_preview", {
    authRequired: false,
    supportedActions: ["memory_query_preview", "memory_status_preview"],
    forbiddenCapabilities: ["memory_write", "vector_write", "embedding_generation", "external_network"],
    responseShapeSummary: "{ previewOnly, matches, queryPreview, memoryWriteEnabled, vectorDatabaseEnabled, embeddingGenerationEnabled, policyFlags }",
    smokeTestKind: "preview_only",
    appUseCases: ["Memory retrieval preview", "Memory status check"],
    safeExamplePayload: { query: "customer order summary", dryRunOnly: true },
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview-only memory retrieval; no memory/vector/embedding write or network call.",
});
const PROVIDER_REGISTRY_PREVIEW = createBaseRoute("provider_registry_preview", "POST /v1/provider-registry/preview", "POST", "provider_registry_preview", {
    authRequired: false,
    supportedActions: ["catalog_preview", "env_diagnostics_preview", "capability_preview", "select_preview", "call_block_preview", "summary_preview"],
    forbiddenCapabilities: ["provider_call", "provider_key_exposure", "sdk_initialization", "external_network"],
    responseShapeSummary: "{ previewOnly, catalog, diagnostics, selectedAdapter, providerCallsEnabled, policyFlags }",
    smokeTestKind: "preview_only",
    appUseCases: ["Provider adapter catalog preview", "Provider call block preview"],
    safeExamplePayload: { action: "catalog_preview" },
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview-only provider registry; no provider SDK, no key exposure, no outbound call.",
});
const APPROVAL_EXECUTION_PREVIEW = createBaseRoute("approval_execution_preview", "POST /v1/approval-execution/preview", "POST", "approval_execution_preview", {
    authRequired: false,
    supportedActions: [],
    forbiddenCapabilities: ["approved_execution", "execution_unlock", "runtime_bridge_open", "real_execution"],
    responseShapeSummary: "{ superseded, canonicalRoute, realExecutionPerformed }",
    smokeTestKind: "preview_only",
    appUseCases: ["Approval request preview", "Execution block preview"],
    safeExamplePayload: { action: "request_preview", requestedAction: "deploy" },
    safeExpectedStatusWithoutAuth: 410,
    safeExpectedStatusWithAuth: 410,
    notes: "Superseded compatibility tombstone; canonical approval authority is POST /api/ceo/approve.",
});
const ASSISTANT_ORCHESTRATOR_PREVIEW = createBaseRoute("assistant_orchestrator_preview", "POST /v1/assistant/orchestrator/preview", "POST", "assistant_orchestrator_preview", {
    authRequired: false,
    supportedActions: [],
    forbiddenCapabilities: ["ai_inference", "provider_call", "action_execution", "external_network"],
    responseShapeSummary: "{ superseded, canonicalRoute, realExecutionPerformed }",
    smokeTestKind: "preview_only",
    appUseCases: ["Intent classification preview", "Safe module routing preview"],
    safeExamplePayload: { action: "plan_preview", userMessage: "What is the workflow?" },
    safeExpectedStatusWithoutAuth: 410,
    safeExpectedStatusWithAuth: 410,
    notes: "Superseded compatibility tombstone; canonical orchestration authority is POST /api/ceo/turn.",
});
const APP_INTEGRATION_PREVIEW = createBaseRoute("app_integration_preview", "POST /v1/app-integration/preview", "POST", "app_integration_preview", {
    authRequired: false,
    supportedActions: [],
    forbiddenCapabilities: ["payment", "send", "customer_update", "app_mutation", "provider_call", "external_network"],
    responseShapeSummary: "{ superseded, canonicalRoute, canonicalManifest, realExecutionPerformed }",
    smokeTestKind: "preview_only",
    appUseCases: ["Smart Print app context preview", "Drishti Kundali app capability preview"],
    safeExamplePayload: { action: "context_envelope", appId: "smart_print" },
    safeExpectedStatusWithoutAuth: 410,
    safeExpectedStatusWithAuth: 410,
    notes: "Superseded compatibility tombstone; canonical app dispatch uses POST /api/ceo/turn and the seven-app manifest.",
});
const OWNER_OPS_DASHBOARD_PREVIEW = createBaseRoute("owner_ops_dashboard_preview", "POST /v1/owner-ops-dashboard/preview", "POST", "owner_ops_dashboard_preview", {
    authRequired: false,
    supportedActions: ["dashboard_preview", "module_status_preview", "readiness_score_preview", "blocker_summary_preview", "next_actions_preview", "health_preview", "policy_check"],
    forbiddenCapabilities: ["real_execution", "provider_call", "external_network", "mutation", "write"],
    responseShapeSummary: "{ previewOnly, action, dashboard, modules, readiness, blockers, nextActions, health, policyFlags }",
    smokeTestKind: "preview_only",
    appUseCases: ["Owner panel dashboard preview", "Ops module status overview"],
    safeExamplePayload: { action: "dashboard_preview", appId: "smart_print" },
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview-only owner/ops dashboard aggregator; no real execution or external call.",
});
const CORE_PREVIEW_CONTRACT_INDEX_ROUTE = createBaseRoute("core_preview_contract_index", "POST /v1/core/preview-contracts", "POST", "core_preview_contract_index", {
    authRequired: true,
    supportedActions: ["route_catalog", "module_catalog", "smoke_pack_preview", "app_contract_summary", "safety_expectations", "summary", "policy_check"],
    forbiddenCapabilities: [
        "provider_call",
        "external_network",
        "real_execution",
        "approved_execution",
        "mutation",
        "database_write",
        "file_write",
        "audit_write",
        "memory_write",
        "payment",
        "send",
        "real_smoke_execution",
    ],
    responseShapeSummary: "{ previewOnly, action, routeCatalog?, moduleCatalog?, smokePack?, safetyExpectations?, summary?, policyFlags }",
    smokeTestKind: "token_protected",
    appUseCases: ["Central contract index discovery", "Owner panel policy check"],
    safeExamplePayload: { action: "policy_check" },
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Token-protected preview-only route; returns the central contract index catalog and policy checks.",
});
const PROVIDER_SETTINGS_PREVIEW_ROUTE = createBaseRoute("provider_settings_preview", "GET /settings/providers", "GET", "provider_settings_preview", {
    authRequired: false,
    supportedActions: [],
    forbiddenCapabilities: [
        "provider_call",
        "external_network",
        "real_execution",
        "approved_execution",
        "mutation",
        "secret_storage",
        "env_write",
        "file_write",
        "database_write",
        "audit_write",
        "memory_write",
        "payment",
        "send",
        "key_material_exposure",
    ],
    responseShapeSummary: "{ superseded, canonicalReadRoute, canonicalWriteRoute, realExecutionPerformed }",
    smokeTestKind: "preview_only",
    appUseCases: ["Owner provider settings HTML preview", "Safe public settings page with no secret"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 410,
    safeExpectedStatusWithAuth: 410,
    notes: "Superseded compatibility tombstone; canonical settings authority is /api/settings/control-plane.",
});
const PROVIDER_SETTINGS_STATUS_ROUTE = createBaseRoute("provider_settings_status", "GET /v1/core/provider-settings", "GET", "provider_settings_preview", {
    authRequired: false,
    supportedActions: [],
    forbiddenCapabilities: [
        "provider_call",
        "external_network",
        "real_execution",
        "approved_execution",
        "mutation",
        "secret_storage",
        "env_write",
        "file_write",
        "database_write",
        "audit_write",
        "memory_write",
        "payment",
        "send",
        "key_material_exposure",
    ],
    responseShapeSummary: "{ superseded, canonicalReadRoute, canonicalWriteRoute, realExecutionPerformed }",
    smokeTestKind: "preview_only",
    appUseCases: ["Owner provider settings JSON status"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 410,
    safeExpectedStatusWithAuth: 410,
    notes: "Superseded compatibility tombstone; canonical settings authority is /api/settings/control-plane.",
});
const PROVIDER_SETTINGS_PREVIEW_POST_ROUTE = createBaseRoute("provider_settings_preview_post", "POST /v1/core/provider-settings/preview", "POST", "provider_settings_preview", {
    authRequired: false,
    supportedActions: [],
    forbiddenCapabilities: [
        "provider_call",
        "external_network",
        "real_execution",
        "approved_execution",
        "mutation",
        "secret_storage",
        "env_write",
        "file_write",
        "database_write",
        "audit_write",
        "memory_write",
        "payment",
        "send",
        "key_material_exposure",
    ],
    responseShapeSummary: "{ superseded, canonicalReadRoute, canonicalWriteRoute, realExecutionPerformed }",
    smokeTestKind: "preview_only",
    appUseCases: ["Owner provider settings preview actions", "Safe redaction preview", "Settings schema preview"],
    safeExamplePayload: { action: "policy_check" },
    safeExpectedStatusWithoutAuth: 410,
    safeExpectedStatusWithAuth: 410,
    notes: "Superseded compatibility tombstone; canonical settings authority is /api/settings/control-plane.",
});
const KNOWLEDGE_RAG_PREVIEW_ROUTE = createBaseRoute("knowledge_rag_preview", "GET /knowledge", "GET", "knowledge_rag_preview", {
    authRequired: false,
    supportedActions: ["html_preview"],
    forbiddenCapabilities: [
        "file_upload",
        "document_parsing",
        "ocr",
        "external_url_fetch",
        "website_crawling",
        "embedding_generation",
        "vector_database",
        "vector_write",
        "memory_write",
        "audit_write",
        "file_write",
        "database_write",
        "provider_call",
        "external_network",
        "real_execution",
        "approved_execution",
        "mutation",
        "payment",
        "send",
        "key_material_exposure",
    ],
    responseShapeSummary: "text/html; previewOnly, uploadEnabled=false, documentParsingEnabled=false, embeddingGenerationEnabled=false, vectorWriteExecuted=false, memoryWriteExecuted=false",
    smokeTestKind: "public_health",
    appUseCases: ["Knowledge / RAG upload HTML preview", "Safe public knowledge page with no upload or secret"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Public static HTML page with no file upload, no parser, no URL fetch, no crawling, no embedding, no secret.",
});
const KNOWLEDGE_RAG_STATUS_ROUTE = createBaseRoute("knowledge_rag_status", "GET /v1/core/knowledge", "GET", "knowledge_rag_preview", {
    authRequired: true,
    supportedActions: ["status_preview"],
    forbiddenCapabilities: [
        "file_upload",
        "document_parsing",
        "ocr",
        "external_url_fetch",
        "website_crawling",
        "embedding_generation",
        "vector_database",
        "vector_write",
        "memory_write",
        "audit_write",
        "file_write",
        "database_write",
        "provider_call",
        "external_network",
        "real_execution",
        "approved_execution",
        "mutation",
        "payment",
        "send",
        "key_material_exposure",
    ],
    responseShapeSummary: "{ previewOnly, status, policyFlags, sourceCatalog, uploadEnabled, documentParsingEnabled, embeddingGenerationEnabled, vectorWriteExecuted }",
    smokeTestKind: "token_protected",
    appUseCases: ["Knowledge / RAG upload JSON status"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Token-protected safe JSON status; no file upload, no parser, no URL fetch, no embedding, no vector write.",
});
const KNOWLEDGE_RAG_PREVIEW_POST_ROUTE = createBaseRoute("knowledge_rag_preview_post", "POST /v1/core/knowledge/preview", "POST", "knowledge_rag_preview", {
    authRequired: true,
    supportedActions: [
        "status_preview",
        "html_preview",
        "summary",
        "policy_check",
        "upload_schema_preview",
        "chunking_preview",
        "redaction_preview",
        "retrieval_preview",
        "source_catalog_preview",
    ],
    forbiddenCapabilities: [
        "file_upload",
        "document_parsing",
        "ocr",
        "external_url_fetch",
        "website_crawling",
        "embedding_generation",
        "vector_database",
        "vector_write",
        "memory_write",
        "audit_write",
        "file_write",
        "database_write",
        "provider_call",
        "external_network",
        "real_execution",
        "approved_execution",
        "mutation",
        "payment",
        "send",
        "key_material_exposure",
    ],
    responseShapeSummary: "{ action, status, html, summary, schema, chunking, redaction, retrieval, sourceCatalog, policyFlags, policyCompliant }",
    smokeTestKind: "token_protected",
    appUseCases: ["Knowledge / RAG upload preview actions", "Safe chunking/redaction/retrieval preview", "Upload schema preview"],
    safeExamplePayload: { action: "policy_check" },
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview actions for knowledge / RAG uploads; requires local auth token; no real upload, parser, fetch, embedding, or vector DB call.",
});
const MEMORY_APPROVAL_PREVIEW_ROUTE = createBaseRoute("memory_approval_preview", "GET /memory", "GET", "memory_approval_preview", {
    authRequired: false,
    supportedActions: ["html_preview"],
    forbiddenCapabilities: [
        "memory_write",
        "memory_persistence",
        "vector_database",
        "vector_write",
        "embedding_generation",
        "customer_record_update",
        "database_write",
        "file_write",
        "audit_write",
        "provider_call",
        "external_network",
        "real_execution",
        "approved_execution",
        "mutation",
        "payment",
        "send",
        "key_material_exposure",
        "mcp_live_execution",
        "goose_execution",
        "browser_automation",
        "whatsapp_send",
        "telegram_send",
        "email_send",
    ],
    responseShapeSummary: "text/html; previewOnly, memoryWriteEnabled=false, memoryPersistenceEnabled=false, vectorDatabaseEnabled=false, embeddingGenerationEnabled=false, customerRecordUpdated=false",
    smokeTestKind: "public_health",
    appUseCases: ["Memory write approval HTML preview", "Safe public memory page with no secret or save control"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Public static HTML page with no memory write, no persistence, no secret, no input/button/form/script.",
});
const MEMORY_APPROVAL_STATUS_ROUTE = createBaseRoute("memory_approval_status", "GET /v1/core/memory", "GET", "memory_approval_preview", {
    authRequired: true,
    supportedActions: ["status_preview"],
    forbiddenCapabilities: [
        "memory_write",
        "memory_persistence",
        "vector_database",
        "vector_write",
        "embedding_generation",
        "customer_record_update",
        "database_write",
        "file_write",
        "audit_write",
        "provider_call",
        "external_network",
        "real_execution",
        "approved_execution",
        "mutation",
        "payment",
        "send",
        "key_material_exposure",
        "mcp_live_execution",
        "goose_execution",
        "browser_automation",
        "whatsapp_send",
        "telegram_send",
        "email_send",
    ],
    responseShapeSummary: "{ previewOnly, status, policyFlags, categoryCatalog, memoryWriteEnabled, memoryPersistenceEnabled, vectorDatabaseEnabled, embeddingGenerationEnabled, customerRecordUpdated }",
    smokeTestKind: "token_protected",
    appUseCases: ["Memory write approval JSON status"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Token-protected safe JSON status; no memory write, no persistence, no vector DB, no embedding, no customer update.",
});
const MEMORY_APPROVAL_PREVIEW_POST_ROUTE = createBaseRoute("memory_approval_preview_post", "POST /v1/core/memory/preview", "POST", "memory_approval_preview", {
    authRequired: true,
    supportedActions: [
        "status_preview",
        "html_preview",
        "summary",
        "policy_check",
        "memory_suggestion_preview",
        "approval_flow_preview",
        "redaction_preview",
        "retention_policy_preview",
        "forget_flow_preview",
    ],
    forbiddenCapabilities: [
        "memory_write",
        "memory_persistence",
        "vector_database",
        "vector_write",
        "embedding_generation",
        "customer_record_update",
        "database_write",
        "file_write",
        "audit_write",
        "provider_call",
        "external_network",
        "real_execution",
        "approved_execution",
        "mutation",
        "payment",
        "send",
        "key_material_exposure",
        "mcp_live_execution",
        "goose_execution",
        "browser_automation",
        "whatsapp_send",
        "telegram_send",
        "email_send",
    ],
    responseShapeSummary: "{ action, status, html, summary, suggestion, approvalFlow, redaction, retention, forgetFlow, policyFlags, policyCompliant }",
    smokeTestKind: "token_protected",
    appUseCases: ["Memory write approval preview actions", "Safe suggestion/redaction/retention/forget flow preview"],
    safeExamplePayload: { action: "policy_check" },
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview actions for memory write approval; requires local auth token; no real memory write, persistence, vector DB, or embedding.",
});
const GOOSE_CONNECTOR_PREVIEW_ROUTE = createBaseRoute("goose_connector_preview", "GET /goose", "GET", "goose_connector_preview", {
    authRequired: false,
    supportedActions: ["html_preview"],
    forbiddenCapabilities: [
        "goose_execution",
        "shell_execution",
        "process_spawn",
        "command_execution",
        "file_read",
        "file_write",
        "workspace_mutation",
        "git_mutation",
        "browser_automation",
        "mcp_live_tool_execution",
        "provider_call",
        "external_network",
        "real_execution",
        "approved_execution",
        "mutation",
        "database_write",
        "audit_write",
        "memory_write",
        "payment",
        "send",
        "key_material_exposure",
        "whatsapp_send",
        "telegram_send",
        "email_send",
    ],
    responseShapeSummary: "text/html; previewOnly, gooseExecutionEnabled=false, shellExecutionEnabled=false, fileWriteExecuted=false, workspaceMutationEnabled=false",
    smokeTestKind: "public_health",
    appUseCases: ["Goose connector HTML preview", "Safe public Goose page with no execution or secret"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Public static HTML page with no Goose execution, no shell/process, no file/workspace/git mutation, no script, no secret.",
});
const GOOSE_CONNECTOR_STATUS_ROUTE = createBaseRoute("goose_connector_status", "GET /v1/core/goose", "GET", "goose_connector_preview", {
    authRequired: true,
    supportedActions: ["status_preview"],
    forbiddenCapabilities: [
        "goose_execution",
        "shell_execution",
        "process_spawn",
        "command_execution",
        "file_read",
        "file_write",
        "workspace_mutation",
        "git_mutation",
        "browser_automation",
        "mcp_live_tool_execution",
        "provider_call",
        "external_network",
        "real_execution",
        "approved_execution",
        "mutation",
        "database_write",
        "audit_write",
        "memory_write",
        "payment",
        "send",
        "key_material_exposure",
        "whatsapp_send",
        "telegram_send",
        "email_send",
    ],
    responseShapeSummary: "{ previewOnly, status, policyFlags, commandCatalog, gooseExecutionEnabled, shellExecutionEnabled, commandExecutionPerformed }",
    smokeTestKind: "token_protected",
    appUseCases: ["Goose connector JSON status"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Token-protected safe JSON status; no Goose execution, shell, process, file/workspace/git mutation, or provider call.",
});
const GOOSE_CONNECTOR_PREVIEW_POST_ROUTE = createBaseRoute("goose_connector_preview_post", "POST /v1/core/goose/preview", "POST", "goose_connector_preview", {
    authRequired: true,
    supportedActions: [
        "status_preview",
        "html_preview",
        "summary",
        "policy_check",
        "command_catalog_preview",
        "execution_plan_preview",
        "approval_flow_preview",
        "sandbox_policy_preview",
        "rollback_plan_preview",
    ],
    forbiddenCapabilities: [
        "goose_execution",
        "shell_execution",
        "process_spawn",
        "command_execution",
        "file_read",
        "file_write",
        "workspace_mutation",
        "git_mutation",
        "browser_automation",
        "mcp_live_tool_execution",
        "provider_call",
        "external_network",
        "real_execution",
        "approved_execution",
        "mutation",
        "database_write",
        "audit_write",
        "memory_write",
        "payment",
        "send",
        "key_material_exposure",
        "whatsapp_send",
        "telegram_send",
        "email_send",
    ],
    responseShapeSummary: "{ action, status, html, summary, commandCatalog, executionPlan, approvalFlow, sandboxPolicy, rollbackPlan, policyFlags, policyCompliant }",
    smokeTestKind: "token_protected",
    appUseCases: ["Goose connector preview actions", "Safe command catalog/execution plan/approval/sandbox/rollback preview"],
    safeExamplePayload: { action: "policy_check" },
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview actions for Goose connector; requires local auth token; no real Goose execution, shell, process, file/workspace/git mutation, or provider call.",
});
const BROWSER_EXTENSION_PREVIEW_ROUTE = createBaseRoute("browser_extension_preview", "GET /browser-extension", "GET", "browser_extension_preview", {
    authRequired: false,
    supportedActions: ["html_preview"],
    forbiddenCapabilities: [
        "extension_build",
        "installable_manifest",
        "content_script",
        "background_worker",
        "browser_automation",
        "dom_access",
        "page_scraping",
        "website_fetch",
        "website_crawling",
        "local_storage",
        "session_storage",
        "indexed_db",
        "service_worker",
        "external_network",
        "cdn",
        "provider_call",
        "mcp_live_tool_execution",
        "goose_execution",
        "real_execution",
        "approved_execution",
        "mutation",
        "database_write",
        "file_write",
        "audit_write",
        "memory_write",
        "embedding_generation",
        "vector_write",
        "payment",
        "send",
        "key_material_exposure",
        "whatsapp_send",
        "telegram_send",
        "email_send",
    ],
    responseShapeSummary: "text/html; previewOnly, extensionBuildEnabled=false, installableManifestEnabled=false, contentScriptEnabled=false, browserAutomationEnabled=false, pageScrapingEnabled=false",
    smokeTestKind: "public_health",
    appUseCases: ["Browser extension HTML preview", "Safe public browser extension page with no install or execution"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Public static HTML page with no real extension build, no installable manifest, no content script, no background worker, no browser automation, no DOM access, no page scraping, no script, no secret.",
});
const BROWSER_EXTENSION_STATUS_ROUTE = createBaseRoute("browser_extension_status", "GET /v1/core/browser-extension", "GET", "browser_extension_preview", {
    authRequired: true,
    supportedActions: ["status_preview"],
    forbiddenCapabilities: [
        "extension_build",
        "installable_manifest",
        "content_script",
        "background_worker",
        "browser_automation",
        "dom_access",
        "page_scraping",
        "website_fetch",
        "website_crawling",
        "local_storage",
        "session_storage",
        "indexed_db",
        "service_worker",
        "external_network",
        "cdn",
        "provider_call",
        "mcp_live_tool_execution",
        "goose_execution",
        "real_execution",
        "approved_execution",
        "mutation",
        "database_write",
        "file_write",
        "audit_write",
        "memory_write",
        "embedding_generation",
        "vector_write",
        "payment",
        "send",
        "key_material_exposure",
        "whatsapp_send",
        "telegram_send",
        "email_send",
    ],
    responseShapeSummary: "{ previewOnly, status, policyFlags, permissionCatalog, extensionBuildEnabled, installableManifestEnabled, contentScriptEnabled, browserAutomationEnabled, pageScrapingEnabled }",
    smokeTestKind: "token_protected",
    appUseCases: ["Browser extension JSON status"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Token-protected safe JSON status; no real extension build, no installable manifest, no content script, no background worker, no browser automation, no DOM access, no page scraping, no provider call.",
});
const BROWSER_EXTENSION_PREVIEW_POST_ROUTE = createBaseRoute("browser_extension_preview_post", "POST /v1/core/browser-extension/preview", "POST", "browser_extension_preview", {
    authRequired: true,
    supportedActions: [
        "status_preview",
        "html_preview",
        "summary",
        "policy_check",
        "permission_catalog_preview",
        "page_context_preview",
        "redaction_preview",
        "action_draft_preview",
        "extension_manifest_preview",
    ],
    forbiddenCapabilities: [
        "extension_build",
        "installable_manifest",
        "content_script",
        "background_worker",
        "browser_automation",
        "dom_access",
        "page_scraping",
        "website_fetch",
        "website_crawling",
        "local_storage",
        "session_storage",
        "indexed_db",
        "service_worker",
        "external_network",
        "cdn",
        "provider_call",
        "mcp_live_tool_execution",
        "goose_execution",
        "real_execution",
        "approved_execution",
        "mutation",
        "database_write",
        "file_write",
        "audit_write",
        "memory_write",
        "embedding_generation",
        "vector_write",
        "payment",
        "send",
        "key_material_exposure",
        "whatsapp_send",
        "telegram_send",
        "email_send",
    ],
    responseShapeSummary: "{ action, status, html, summary, permissionCatalog, pageContext, redaction, actionDraft, manifest, policyFlags, policyCompliant }",
    smokeTestKind: "token_protected",
    appUseCases: ["Browser extension preview actions", "Safe permission/page-context/redaction/action-draft/manifest preview"],
    safeExamplePayload: { action: "policy_check" },
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview actions for browser extension; requires local auth token; no real extension build, no installable manifest, no content script, no background worker, no browser automation, no DOM access, no page scraping, no provider call.",
});
const MESSAGING_CONNECTOR_PREVIEW_ROUTE = createBaseRoute("messaging_connector_preview", "GET /messaging", "GET", "messaging_connector_preview", {
    authRequired: false,
    supportedActions: ["html_preview"],
    forbiddenCapabilities: [
        "whatsapp_api_call",
        "telegram_api_call",
        "whatsapp_send",
        "telegram_send",
        "email_send",
        "sms_send",
        "webhook_registration",
        "inbound_webhook_processing",
        "message_send",
        "message_persistence",
        "contact_update",
        "customer_record_update",
        "provider_call",
        "external_network",
        "cdn",
        "real_execution",
        "approved_execution",
        "mutation",
        "database_write",
        "file_write",
        "audit_write",
        "memory_write",
        "embedding_generation",
        "vector_write",
        "payment",
        "key_material_exposure",
        "local_storage",
        "session_storage",
        "indexed_db",
        "service_worker",
    ],
    responseShapeSummary: "text/html; previewOnly, whatsappEnabled=false, telegramEnabled=false, whatsappApiCallExecuted=false, telegramApiCallExecuted=false, sendEnabled=false, webhookRegistrationEnabled=false",
    smokeTestKind: "public_health",
    appUseCases: ["WhatsApp / Telegram connector HTML preview", "Safe public messaging page with no API call or secret"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Public static HTML page with no real WhatsApp/Telegram API call, no webhook registration, no real inbound processing, no message send, no message persistence, no script, no secret.",
});
const MESSAGING_CONNECTOR_STATUS_ROUTE = createBaseRoute("messaging_connector_status", "GET /v1/core/messaging", "GET", "messaging_connector_preview", {
    authRequired: true,
    supportedActions: ["status_preview"],
    forbiddenCapabilities: [
        "whatsapp_api_call",
        "telegram_api_call",
        "whatsapp_send",
        "telegram_send",
        "email_send",
        "sms_send",
        "webhook_registration",
        "inbound_webhook_processing",
        "message_send",
        "message_persistence",
        "contact_update",
        "customer_record_update",
        "provider_call",
        "external_network",
        "cdn",
        "real_execution",
        "approved_execution",
        "mutation",
        "database_write",
        "file_write",
        "audit_write",
        "memory_write",
        "embedding_generation",
        "vector_write",
        "payment",
        "key_material_exposure",
        "local_storage",
        "session_storage",
        "indexed_db",
        "service_worker",
    ],
    responseShapeSummary: "{ previewOnly, status, policyFlags, channelCatalog, whatsappEnabled, telegramEnabled, sendEnabled, webhookRegistrationEnabled }",
    smokeTestKind: "token_protected",
    appUseCases: ["WhatsApp / Telegram connector JSON status"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Token-protected safe JSON status; no real WhatsApp/Telegram API call, no webhook registration, no real inbound processing, no message send, no provider call.",
});
const MESSAGING_CONNECTOR_PREVIEW_POST_ROUTE = createBaseRoute("messaging_connector_preview_post", "POST /v1/core/messaging/preview", "POST", "messaging_connector_preview", {
    authRequired: true,
    supportedActions: [
        "status_preview",
        "html_preview",
        "summary",
        "policy_check",
        "channel_catalog_preview",
        "inbound_message_preview",
        "redaction_preview",
        "reply_draft_preview",
        "send_policy_preview",
        "webhook_schema_preview",
    ],
    forbiddenCapabilities: [
        "whatsapp_api_call",
        "telegram_api_call",
        "whatsapp_send",
        "telegram_send",
        "email_send",
        "sms_send",
        "webhook_registration",
        "inbound_webhook_processing",
        "message_send",
        "message_persistence",
        "contact_update",
        "customer_record_update",
        "provider_call",
        "external_network",
        "cdn",
        "real_execution",
        "approved_execution",
        "mutation",
        "database_write",
        "file_write",
        "audit_write",
        "memory_write",
        "embedding_generation",
        "vector_write",
        "payment",
        "key_material_exposure",
        "local_storage",
        "session_storage",
        "indexed_db",
        "service_worker",
    ],
    responseShapeSummary: "{ action, status, html, summary, channelCatalog, inboundMessage, redaction, replyDraft, sendPolicy, webhookSchema, policyFlags, policyCompliant }",
    smokeTestKind: "token_protected",
    appUseCases: ["WhatsApp / Telegram connector preview actions", "Safe channel/inbound/redaction/reply/send-policy/webhook preview"],
    safeExamplePayload: { action: "policy_check" },
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview actions for WhatsApp / Telegram connector; requires local auth token; no real API call, no webhook registration, no real inbound processing, no message send, no provider call.",
});
const PREVIEW_APPS_HOME_DASHBOARD_ROUTE = createBaseRoute("preview_apps_home_dashboard", "GET /", "GET", "preview_apps_home_dashboard", {
    authRequired: false,
    supportedActions: ["html_preview"],
    forbiddenCapabilities: [
        "provider_call",
        "external_network",
        "cdn",
        "real_execution",
        "approved_execution",
        "mutation",
        "database_write",
        "file_write",
        "audit_write",
        "memory_write",
        "embedding_generation",
        "vector_write",
        "payment",
        "send",
        "key_material_exposure",
        "local_storage",
        "session_storage",
        "indexed_db",
        "service_worker",
        "whatsapp_api_call",
        "telegram_api_call",
        "goose_execution",
    ],
    responseShapeSummary: "text/html; previewOnly, static app catalog, route index, safety summary, next safe actions",
    smokeTestKind: "public_health",
    appUseCases: ["Public preview apps home HTML page", "Safe home dashboard linking to all public preview apps"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Public read-only home HTML page with no real execution, no provider call, no external network, no secret, no mutation, no storage, no service worker.",
});
const PREVIEW_APPS_HOME_APPS_ROUTE = createBaseRoute("preview_apps_home_apps", "GET /apps", "GET", "preview_apps_home_dashboard", {
    authRequired: false,
    supportedActions: ["html_preview"],
    forbiddenCapabilities: [
        "provider_call",
        "external_network",
        "cdn",
        "real_execution",
        "approved_execution",
        "mutation",
        "database_write",
        "file_write",
        "audit_write",
        "memory_write",
        "embedding_generation",
        "vector_write",
        "payment",
        "send",
        "key_material_exposure",
        "local_storage",
        "session_storage",
        "indexed_db",
        "service_worker",
        "whatsapp_api_call",
        "telegram_api_call",
        "goose_execution",
    ],
    responseShapeSummary: "text/html; previewOnly, compact app catalog and route index",
    smokeTestKind: "public_health",
    appUseCases: ["Public /apps page listing all preview apps"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Public read-only /apps HTML page with no real execution, no provider call, no secret, no mutation, no storage.",
});
const PREVIEW_APPS_HOME_STATUS_ROUTE = createBaseRoute("preview_apps_home_status", "GET /v1/core/apps", "GET", "preview_apps_home_dashboard", {
    authRequired: true,
    supportedActions: ["status_preview", "route_index_preview", "navigation_preview"],
    forbiddenCapabilities: [
        "provider_call",
        "external_network",
        "cdn",
        "real_execution",
        "approved_execution",
        "mutation",
        "database_write",
        "file_write",
        "audit_write",
        "memory_write",
        "embedding_generation",
        "vector_write",
        "payment",
        "send",
        "key_material_exposure",
        "local_storage",
        "session_storage",
        "indexed_db",
        "service_worker",
        "whatsapp_api_call",
        "telegram_api_call",
        "goose_execution",
    ],
    responseShapeSummary: "{ previewOnly, status, appCatalog, routeIndex, navigation, policyFlags, policyCompliant }",
    smokeTestKind: "token_protected",
    appUseCases: ["Preview apps home JSON status", "Safe app catalog and route index behind local auth"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Token-protected safe JSON status; no real execution, no provider call, no external network, no secret exposure.",
});
const PREVIEW_APPS_HOME_PREVIEW_POST_ROUTE = createBaseRoute("preview_apps_home_preview_post", "POST /v1/core/apps/preview", "POST", "preview_apps_home_dashboard", {
    authRequired: true,
    supportedActions: [
        "status_preview",
        "html_preview",
        "summary",
        "policy_check",
        "app_catalog_preview",
        "route_index_preview",
        "navigation_preview",
    ],
    forbiddenCapabilities: [
        "provider_call",
        "external_network",
        "cdn",
        "real_execution",
        "approved_execution",
        "mutation",
        "database_write",
        "file_write",
        "audit_write",
        "memory_write",
        "embedding_generation",
        "vector_write",
        "payment",
        "send",
        "key_material_exposure",
        "local_storage",
        "session_storage",
        "indexed_db",
        "service_worker",
        "whatsapp_api_call",
        "telegram_api_call",
        "goose_execution",
    ],
    responseShapeSummary: "{ action, status, html, summary, appCatalog, routeIndex, navigation, policyFlags, policyCompliant }",
    smokeTestKind: "token_protected",
    appUseCases: ["Preview apps home preview actions", "Safe app catalog / route index / navigation preview only"],
    safeExamplePayload: { action: "policy_check" },
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview actions for the home dashboard; requires local auth token; no real execution, no provider call, no external network, no mutation, no secret exposure.",
});
const EXTERNAL_SERVICE_TOOLKIT_PREVIEW_ROUTE = createBaseRoute("external_service_toolkit_preview", "GET /external-services", "GET", "external_service_toolkit_preview", {
    authRequired: false,
    supportedActions: ["html_preview"],
    forbiddenCapabilities: ["provider_call", "external_network", "real_execution", "mutation", "database_write", "file_write", "audit_write", "memory_write", "payment", "send", "key_material_exposure"],
    responseShapeSummary: "text/html; previewOnly, external service toolkit catalog and policy preview",
    smokeTestKind: "public_health",
    appUseCases: ["Public external service toolkit preview HTML page"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Public read-only external service toolkit HTML page; no real API call, no provider SDK, no key, no send, no upload, no screenshot, no PDF.",
});
const EXTERNAL_SERVICE_TOOLKIT_STATUS_ROUTE = createBaseRoute("external_service_toolkit_status", "GET /v1/core/external-services", "GET", "external_service_toolkit_preview", {
    authRequired: true,
    supportedActions: ["status_preview"],
    forbiddenCapabilities: ["provider_call", "external_network", "real_execution", "mutation", "database_write", "file_write", "audit_write", "memory_write", "payment", "send", "key_material_exposure"],
    responseShapeSummary: "{ status, policyFlags, previewOnly }",
    smokeTestKind: "token_protected",
    appUseCases: ["Protected external service toolkit JSON status"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Token-protected external service toolkit status; no real API call, no provider SDK, no key, no mutation.",
});
const EXTERNAL_SERVICE_TOOLKIT_PREVIEW_POST_ROUTE = createBaseRoute("external_service_toolkit_preview_post", "POST /v1/core/external-services/preview", "POST", "external_service_toolkit_preview", {
    authRequired: true,
    supportedActions: ["status_preview", "html_preview", "summary", "policy_check", "service_catalog_preview", "tool_permission_preview", "provider_selection_preview", "email_validation_preview", "phone_validation_preview", "location_lookup_preview", "currency_rate_preview", "file_upload_policy_preview", "screenshot_policy_preview", "document_pdf_policy_preview", "calendar_holidays_preview", "notification_draft_preview", "redaction_preview"],
    forbiddenCapabilities: ["provider_call", "external_network", "real_execution", "mutation", "database_write", "file_write", "audit_write", "memory_write", "payment", "send", "key_material_exposure"],
    responseShapeSummary: "{ action, result|catalog|permissions|policy|status|summary, policyFlags, previewOnly }",
    smokeTestKind: "token_protected",
    appUseCases: ["Preview actions for external service toolkit"],
    safeExamplePayload: { action: "status_preview" },
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview actions for external service toolkit; requires local auth token; no real API call, no provider SDK, no key, no send, no upload, no screenshot, no PDF.",
});
const RESEARCH_ENGINE_PREVIEW_ROUTE = createBaseRoute("research_engine_preview", "GET /research-engine", "GET", "research_engine_preview", {
    authRequired: false,
    supportedActions: ["html_preview"],
    forbiddenCapabilities: ["python_execution", "subprocess", "internet_fetch", "source_fetch", "report_write", "provider_call", "external_network", "real_execution", "mutation", "database_write", "file_write", "audit_write", "memory_write", "payment", "send", "key_material_exposure"],
    responseShapeSummary: "text/html; previewOnly, research engine preview page with research types and source catalog",
    smokeTestKind: "public_health",
    appUseCases: ["Public research engine preview HTML page"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Public read-only research engine HTML page; no Python execution, no subprocess, no internet fetch, no report write.",
});
const RESEARCH_ENGINE_STATUS_ROUTE = createBaseRoute("research_engine_status", "GET /v1/core/research-engine", "GET", "research_engine_preview", {
    authRequired: true,
    supportedActions: ["status_preview"],
    forbiddenCapabilities: ["python_execution", "subprocess", "internet_fetch", "source_fetch", "report_write", "provider_call", "external_network", "real_execution", "mutation", "database_write", "file_write", "audit_write", "memory_write", "payment", "send", "key_material_exposure"],
    responseShapeSummary: "{ status, policyFlags, previewOnly }",
    smokeTestKind: "token_protected",
    appUseCases: ["Protected research engine JSON status"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Token-protected research engine status; no Python execution, no subprocess, no internet fetch, no report write.",
});
const RESEARCH_ENGINE_PREVIEW_POST_ROUTE = createBaseRoute("research_engine_preview_post", "POST /v1/core/research-engine/preview", "POST", "research_engine_preview", {
    authRequired: true,
    supportedActions: ["status_preview", "html_preview", "summary", "policy_check", "research_topic_preview", "source_catalog_preview", "research_plan_preview", "report_schema_preview", "sample_report_preview", "python_worker_policy_preview", "approval_flow_preview", "redaction_preview", "output_safety_preview"],
    forbiddenCapabilities: ["python_execution", "subprocess", "internet_fetch", "source_fetch", "report_write", "provider_call", "external_network", "real_execution", "mutation", "database_write", "file_write", "audit_write", "memory_write", "payment", "send", "key_material_exposure"],
    responseShapeSummary: "{ action, result|catalog|plan|schema|policy|flow|safety, policyFlags, previewOnly }",
    smokeTestKind: "token_protected",
    appUseCases: ["Preview actions for research engine"],
    safeExamplePayload: { action: "status_preview" },
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview actions for research engine; requires local auth token; no Python execution, no subprocess, no internet fetch, no report write.",
});
const TOOL_APPROVAL_WORKFLOW_PREVIEW_ROUTE = createBaseRoute("tool_approval_workflow_preview", "GET /approvals", "GET", "tool_approval_workflow_preview", {
    authRequired: false,
    supportedActions: ["html_preview"],
    forbiddenCapabilities: ["approval_persistence", "real_execution", "execution_unlock", "send", "write", "payment", "external_api", "python_execution", "key_material_exposure"],
    responseShapeSummary: "{ contentType, html, previewOnly }",
    smokeTestKind: "preview_only",
    appUseCases: ["Public read-only HTML tool approval workflow preview"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Public HTML preview of tool approval workflow; no real persistence, no execution, no send, no write, no payment.",
});
const TOOL_APPROVAL_WORKFLOW_STATUS_ROUTE = createBaseRoute("tool_approval_workflow_status", "GET /v1/core/approvals", "GET", "tool_approval_workflow_preview", {
    authRequired: true,
    supportedActions: ["status_preview"],
    forbiddenCapabilities: ["approval_persistence", "real_execution", "execution_unlock", "send", "write", "payment", "external_api", "python_execution", "key_material_exposure"],
    responseShapeSummary: "{ previewOnly, status, policyFlags }",
    smokeTestKind: "token_protected",
    appUseCases: ["Protected tool approval workflow JSON status"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Token-protected tool approval workflow status; no real persistence, no execution, no send, no write, no payment.",
});
const TOOL_APPROVAL_WORKFLOW_PREVIEW_POST_ROUTE = createBaseRoute("tool_approval_workflow_preview_post", "POST /v1/core/approvals/preview", "POST", "tool_approval_workflow_preview", {
    authRequired: true,
    supportedActions: ["status_preview", "html_preview", "summary", "policy_check", "approval_matrix_preview", "confirmation_request_preview", "risk_assessment_preview", "owner_decision_preview", "approval_receipt_preview", "blocked_action_preview", "notification_confirmation_preview", "email_confirmation_preview", "phone_confirmation_preview", "file_upload_confirmation_preview", "screenshot_confirmation_preview", "pdf_confirmation_preview", "research_confirmation_preview", "messaging_send_confirmation_preview", "payment_confirmation_preview", "redaction_preview"],
    forbiddenCapabilities: ["approval_persistence", "real_execution", "execution_unlock", "send", "write", "payment", "external_api", "python_execution", "key_material_exposure"],
    responseShapeSummary: "{ action, result|status|summary, policyFlags, previewOnly }",
    smokeTestKind: "token_protected",
    appUseCases: ["Preview actions for tool approval workflow"],
    safeExamplePayload: { action: "status_preview" },
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview actions for tool approval workflow; requires local auth token; no real persistence, no execution, no send, no write, no payment.",
});
const SAFE_TOOL_EXECUTION_PLAN_PREVIEW_ROUTE = createBaseRoute("safe_tool_execution_plan_preview", "GET /execution-plans", "GET", "safe_tool_execution_plan_preview", {
    authRequired: false,
    supportedActions: ["html_preview"],
    forbiddenCapabilities: ["real_execution", "adapter_execution", "provider_call", "send", "write", "payment", "python_execution", "rollback_execution", "key_material_exposure"],
    responseShapeSummary: "{ contentType, html, previewOnly }",
    smokeTestKind: "preview_only",
    appUseCases: ["Public read-only HTML safe tool execution plan preview"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Public HTML preview of safe tool execution plan; no real execution, no adapter, no send, no write, no payment.",
});
const SAFE_TOOL_EXECUTION_PLAN_STATUS_ROUTE = createBaseRoute("safe_tool_execution_plan_status", "GET /v1/core/execution-plans", "GET", "safe_tool_execution_plan_preview", {
    authRequired: true,
    supportedActions: ["status_preview"],
    forbiddenCapabilities: ["real_execution", "adapter_execution", "provider_call", "send", "write", "payment", "python_execution", "rollback_execution", "key_material_exposure"],
    responseShapeSummary: "{ previewOnly, status, policyFlags }",
    smokeTestKind: "token_protected",
    appUseCases: ["Protected safe tool execution plan JSON status"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Token-protected safe tool execution plan status; no real execution, no adapter, no send, no write, no payment.",
});
const SAFE_TOOL_EXECUTION_PLAN_PREVIEW_POST_ROUTE = createBaseRoute("safe_tool_execution_plan_preview_post", "POST /v1/core/execution-plans/preview", "POST", "safe_tool_execution_plan_preview", {
    authRequired: true,
    supportedActions: ["status_preview", "html_preview", "summary", "policy_check", "execution_plan_preview", "tool_chain_preview", "preflight_check_preview", "approval_gate_preview", "adapter_selection_preview", "rollback_plan_preview", "audit_plan_preview", "dry_run_result_preview", "blocked_execution_preview", "external_service_execution_preview", "messaging_execution_preview", "file_pdf_screenshot_execution_preview", "research_execution_preview", "payment_mutation_execution_preview", "anshika_agent_execution_preview", "redaction_preview"],
    forbiddenCapabilities: ["real_execution", "adapter_execution", "provider_call", "send", "write", "payment", "python_execution", "rollback_execution", "key_material_exposure"],
    responseShapeSummary: "{ action, result|status|summary, policyFlags, previewOnly }",
    smokeTestKind: "token_protected",
    appUseCases: ["Preview actions for safe tool execution plan"],
    safeExamplePayload: { action: "status_preview" },
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview actions for safe tool execution plan; requires local auth token; no real execution, no adapter, no send, no write, no payment.",
});
const TOOL_ADAPTER_REGISTRY_PREVIEW_ROUTE = createBaseRoute("tool_adapter_registry_preview", "GET /adapters", "GET", "tool_adapter_registry_preview", {
    authRequired: false,
    supportedActions: ["html_preview"],
    forbiddenCapabilities: ["adapter_execution", "adapter_sdk", "provider_sdk", "external_api", "send", "write", "payment", "python_execution"],
    responseShapeSummary: "HTML page (no script, no form, no button)",
    smokeTestKind: "public_health",
    appUseCases: ["Public read-only tool adapter registry preview page"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Public HTML tool adapter registry preview; no token, no adapter execution, no SDK, no send, no write, no payment.",
});
const TOOL_ADAPTER_REGISTRY_STATUS_ROUTE = createBaseRoute("tool_adapter_registry_status", "GET /v1/core/adapters", "GET", "tool_adapter_registry_preview", {
    authRequired: true,
    supportedActions: ["status_preview"],
    forbiddenCapabilities: ["adapter_execution", "adapter_sdk", "provider_sdk", "external_api", "send", "write", "payment", "python_execution"],
    responseShapeSummary: "{ previewOnly, status, policyFlags }",
    smokeTestKind: "token_protected",
    appUseCases: ["Protected tool adapter registry JSON status"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Token-protected tool adapter registry status; no adapter execution, no SDK, no send, no write, no payment.",
});
const TOOL_ADAPTER_REGISTRY_PREVIEW_POST_ROUTE = createBaseRoute("tool_adapter_registry_preview_post", "POST /v1/core/adapters/preview", "POST", "tool_adapter_registry_preview", {
    authRequired: true,
    supportedActions: ["status_preview", "html_preview", "summary", "policy_check", "adapter_registry_preview", "capability_router_preview", "adapter_family_preview", "tool_to_adapter_map_preview", "adapter_selection_preview", "adapter_preflight_preview", "adapter_permission_preview", "adapter_blocklist_preview", "external_service_adapter_preview", "messaging_adapter_preview", "file_pdf_screenshot_adapter_preview", "research_adapter_preview", "anshika_agent_adapter_preview", "payment_adapter_block_preview", "redaction_preview"],
    forbiddenCapabilities: ["adapter_execution", "adapter_sdk", "provider_sdk", "external_api", "send", "write", "payment", "python_execution"],
    responseShapeSummary: "{ action, result|status|summary, policyFlags, previewOnly }",
    smokeTestKind: "token_protected",
    appUseCases: ["Preview actions for tool adapter registry and capability router"],
    safeExamplePayload: { action: "status_preview" },
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview actions for tool adapter registry; requires local auth token; no adapter execution, no SDK, no send, no write, no payment.",
});
const POLICY_DECISION_API_PREVIEW_ROUTE = createBaseRoute("policy_decision_api_preview", "GET /policy-decisions", "GET", "policy_decision_api_preview", {
    authRequired: false,
    supportedActions: ["html_preview"],
    forbiddenCapabilities: ["persistence", "receipt_signing", "execution", "adapter_execution", "external_api", "send", "write", "payment"],
    responseShapeSummary: "{ html }",
    smokeTestKind: "public_health",
    appUseCases: ["View policy decision API preview dashboard"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 200,
    safeExpectedStatusWithAuth: 200,
    notes: "Public policy decision API HTML preview; no token; safe static read-only page.",
});
const POLICY_DECISION_API_STATUS_ROUTE = createBaseRoute("policy_decision_api_status", "GET /v1/core/policy-decisions", "GET", "policy_decision_api_preview", {
    authRequired: true,
    supportedActions: ["status_preview"],
    forbiddenCapabilities: ["persistence", "receipt_signing", "execution", "adapter_execution", "external_api", "send", "write", "payment"],
    responseShapeSummary: "{ status, policyFlags, previewOnly }",
    smokeTestKind: "token_protected",
    appUseCases: ["Read policy decision API status as JSON"],
    safeExamplePayload: {},
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Protected policy decision API JSON status; requires local auth token; no persistence, no execution.",
});
const POLICY_DECISION_API_PREVIEW_POST_ROUTE = createBaseRoute("policy_decision_api_preview_post", "POST /v1/core/policy-decisions/preview", "POST", "policy_decision_api_preview", {
    authRequired: true,
    supportedActions: ["status_preview", "html_preview", "summary", "policy_check", "decision_matrix_preview", "policy_decision_preview", "capability_decision_preview", "approval_requirement_preview", "execution_plan_requirement_preview", "adapter_requirement_preview", "key_requirement_preview", "critical_block_preview", "anshika_policy_decision_preview", "external_service_policy_decision_preview", "messaging_policy_decision_preview", "research_policy_decision_preview", "payment_policy_decision_preview", "decision_receipt_preview", "redaction_preview"],
    forbiddenCapabilities: ["persistence", "receipt_signing", "execution", "adapter_execution", "external_api", "send", "write", "payment", "python_execution"],
    responseShapeSummary: "{ action, result|status|summary, policyFlags, previewOnly }",
    smokeTestKind: "token_protected",
    appUseCases: ["Preview actions for policy decision API"],
    safeExamplePayload: { action: "status_preview" },
    safeExpectedStatusWithoutAuth: 401,
    safeExpectedStatusWithAuth: 200,
    notes: "Preview actions for policy decision API; requires local auth token; no persistence, no execution, no send, no write, no payment.",
});
const CATALOG_BY_ROUTE_ID = {
    gateway_preview: GATEWAY_PREVIEW,
    gateway_observability_preview_status: GATEWAY_OBSERVABILITY_PREVIEW_STATUS,
    gateway_observability_recent_events: GATEWAY_OBSERVABILITY_RECENT_EVENTS,
    security_bumblebee_preview_ingest: SECURITY_BUMBLEBEE_PREVIEW_INGEST,
    security_bumblebee_preview_alert: SECURITY_BUMBLEBEE_PREVIEW_ALERT,
    mcp_readonly_tools_preview: MCP_READONLY_TOOLS_PREVIEW,
    audit_store_preview: AUDIT_STORE_PREVIEW,
    memory_retrieval_preview: MEMORY_RETRIEVAL_PREVIEW,
    provider_registry_preview: PROVIDER_REGISTRY_PREVIEW,
    approval_execution_preview: APPROVAL_EXECUTION_PREVIEW,
    assistant_orchestrator_preview: ASSISTANT_ORCHESTRATOR_PREVIEW,
    app_integration_preview: APP_INTEGRATION_PREVIEW,
    owner_ops_dashboard_preview: OWNER_OPS_DASHBOARD_PREVIEW,
    core_preview_contract_index: CORE_PREVIEW_CONTRACT_INDEX_ROUTE,
    provider_settings_preview: PROVIDER_SETTINGS_PREVIEW_ROUTE,
    provider_settings_status: PROVIDER_SETTINGS_STATUS_ROUTE,
    provider_settings_preview_post: PROVIDER_SETTINGS_PREVIEW_POST_ROUTE,
    knowledge_rag_preview: KNOWLEDGE_RAG_PREVIEW_ROUTE,
    knowledge_rag_status: KNOWLEDGE_RAG_STATUS_ROUTE,
    knowledge_rag_preview_post: KNOWLEDGE_RAG_PREVIEW_POST_ROUTE,
    memory_approval_preview: MEMORY_APPROVAL_PREVIEW_ROUTE,
    memory_approval_status: MEMORY_APPROVAL_STATUS_ROUTE,
    memory_approval_preview_post: MEMORY_APPROVAL_PREVIEW_POST_ROUTE,
    goose_connector_preview: GOOSE_CONNECTOR_PREVIEW_ROUTE,
    goose_connector_status: GOOSE_CONNECTOR_STATUS_ROUTE,
    goose_connector_preview_post: GOOSE_CONNECTOR_PREVIEW_POST_ROUTE,
    browser_extension_preview: BROWSER_EXTENSION_PREVIEW_ROUTE,
    browser_extension_status: BROWSER_EXTENSION_STATUS_ROUTE,
    browser_extension_preview_post: BROWSER_EXTENSION_PREVIEW_POST_ROUTE,
    messaging_connector_preview: MESSAGING_CONNECTOR_PREVIEW_ROUTE,
    messaging_connector_status: MESSAGING_CONNECTOR_STATUS_ROUTE,
    messaging_connector_preview_post: MESSAGING_CONNECTOR_PREVIEW_POST_ROUTE,
    preview_apps_home_dashboard: PREVIEW_APPS_HOME_DASHBOARD_ROUTE,
    preview_apps_home_apps: PREVIEW_APPS_HOME_APPS_ROUTE,
    preview_apps_home_status: PREVIEW_APPS_HOME_STATUS_ROUTE,
    preview_apps_home_preview_post: PREVIEW_APPS_HOME_PREVIEW_POST_ROUTE,
    external_service_toolkit_preview: EXTERNAL_SERVICE_TOOLKIT_PREVIEW_ROUTE,
    external_service_toolkit_status: EXTERNAL_SERVICE_TOOLKIT_STATUS_ROUTE,
    external_service_toolkit_preview_post: EXTERNAL_SERVICE_TOOLKIT_PREVIEW_POST_ROUTE,
    research_engine_preview: RESEARCH_ENGINE_PREVIEW_ROUTE,
    research_engine_status: RESEARCH_ENGINE_STATUS_ROUTE,
    research_engine_preview_post: RESEARCH_ENGINE_PREVIEW_POST_ROUTE,
    tool_approval_workflow_preview: TOOL_APPROVAL_WORKFLOW_PREVIEW_ROUTE,
    tool_approval_workflow_status: TOOL_APPROVAL_WORKFLOW_STATUS_ROUTE,
    tool_approval_workflow_preview_post: TOOL_APPROVAL_WORKFLOW_PREVIEW_POST_ROUTE,
    safe_tool_execution_plan_preview: SAFE_TOOL_EXECUTION_PLAN_PREVIEW_ROUTE,
    safe_tool_execution_plan_status: SAFE_TOOL_EXECUTION_PLAN_STATUS_ROUTE,
    safe_tool_execution_plan_preview_post: SAFE_TOOL_EXECUTION_PLAN_PREVIEW_POST_ROUTE,
    tool_adapter_registry_preview: TOOL_ADAPTER_REGISTRY_PREVIEW_ROUTE,
    tool_adapter_registry_status: TOOL_ADAPTER_REGISTRY_STATUS_ROUTE,
    tool_adapter_registry_preview_post: TOOL_ADAPTER_REGISTRY_PREVIEW_POST_ROUTE,
    policy_decision_api_preview: POLICY_DECISION_API_PREVIEW_ROUTE,
    policy_decision_api_status: POLICY_DECISION_API_STATUS_ROUTE,
    policy_decision_api_preview_post: POLICY_DECISION_API_PREVIEW_POST_ROUTE,
};
function toAlphanumericKey(value) {
    return value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}
const ROUTE_NORMALIZATION = (() => {
    const map = {};
    for (const route of Object.values(CATALOG_BY_ROUTE_ID)) {
        map[toAlphanumericKey(route.routeId)] = route.routeId;
        map[toAlphanumericKey(route.route)] = route.routeId;
    }
    return map;
})();
export function getCorePreviewContractRouteCatalog() {
    return Object.values(CATALOG_BY_ROUTE_ID);
}
export function normalizeCorePreviewContractRouteId(value) {
    if (typeof value !== "string")
        return null;
    const key = toAlphanumericKey(value);
    return ROUTE_NORMALIZATION[key] ?? null;
}
export function findCorePreviewContractRouteEntry(value) {
    const routeId = normalizeCorePreviewContractRouteId(value);
    return routeId ? CATALOG_BY_ROUTE_ID[routeId] : null;
}
//# sourceMappingURL=route-contract-catalog.js.map